import {
  Component,
  computed,
  effect,
  type ElementRef,
  inject,
  input,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { form, FormField } from '@angular/forms/signals';

import type { SearchOption } from '../../services/listings/set-search-options';

import { FilteringService } from '../../services/filtering';
import { SetSearch } from '../../services/filtering/search';
import { StateService } from '../../services/state/state';

@Component({
  imports: [FormField],
  selector: 'app-search-input',
  styleUrl: './search-input.css',
  templateUrl: './search-input.html',
})
export class SearchInput {
  protected readonly filteringService = inject(FilteringService);
  protected readonly stateService = inject(StateService);

  public readonly colorClass = input('bg-white/20 focus:outline-white/20');
  public readonly darkColorClass = input(
    'dark:bg-neutral-700/50 dark:outline-neutral-400/25 dark:placeholder:text-white/50',
  );
  public readonly placeholder = input('');
  public readonly value = input('');

  protected readonly searchOptionsList = viewChild<ElementRef>('searchOptionsList');

  protected readonly class = computed(() => {
    return `col-start-1 row-start-1 block w-full rounded-full py-1.5 pr-3 pl-10 text-base text-white ${this.colorClass()} placeholder:text-white/75 focus:outline-2 focus:-outline-offset-2 sm:text-sm/6 ${this.darkColorClass()}`;
  });
  protected readonly focusedIndex = signal<number>(0);
  protected readonly focusedOption = computed<SearchOption | undefined>(() => {
    const _idx = this.focusedIndex();
    const _options = this.filteringService.searchOptions() as SearchOption[];
    return _idx >= 0 && _idx < _options.length ? _options[_idx] : _options[0];
  });
  protected readonly searchModel = signal({
    term: '',
  });
  protected readonly searchForm = form(this.searchModel);
  protected readonly showingSearchOptions = signal(false);

  private _searchTimeout: ReturnType<typeof setTimeout> | undefined = undefined;

  constructor() {
    effect(() => {
      if (!this.filteringService.userInitiatedSearch() && this.value()) {
        untracked(() => {
          this.searchModel.update((model) => ({ ...model, term: this.value() }));
          void this._search().then(() => {
            this._applySearch();
          });
        });
      }
    });

    // Reset focusedIndex when search options change or dropdown closes
    effect(() => {
      if (this.filteringService.searchOptions().length === 0 || !this.showingSearchOptions()) {
        this.focusedIndex.set(0);
      }
    });

    effect(() => {
      if (!this.filteringService.hasFilters()) {
        this.searchModel.update((model) => ({ ...model, term: this.filteringService.search() }));
      }
    });

    effect(() => {
      if (this.focusedOption()) {
        setTimeout(() => {
          const _focusedElement = document.getElementById(this.focusedOption()?.uxid ?? '');
          if (_focusedElement) {
            _focusedElement.scrollIntoView({ block: 'center', inline: 'nearest' });
          }
        }, 300);
      }
    });
  }

  private _applySearch(option?: SearchOption): void {
    // console.log(option);
    if (option?.key === 'modelTitle') {
      void this.filteringService.resetFilters();
      this.filteringService.models.set([
        option.displayName.replace('Model', '').trim().replaceAll(' ', '-').toLowerCase(),
      ]);
      this.searchModel.update((model) => ({ ...model, term: option.displayName }));
    } else if (option?.key === 'seriesTitle') {
      void this.filteringService.resetFilters();
      this.filteringService.series.set([
        option.displayName.replace('Series', '').trim().replaceAll(' ', '-').toLowerCase(),
      ]);
      this.searchModel.update((model) => ({ ...model, term: option.displayName }));
    } else if (option) {
      void this.filteringService.resetFilters();
      this.filteringService.search.set(option.displayName);
      this.filteringService.ids.set([...new Set(option.ids)]);
      this.searchModel.update((model) => ({ ...model, term: option.displayName }));
    } else {
      this.filteringService.ids.set([
        ...new Set(
          (this.filteringService.searchOptions() as SearchOption[]).flatMap((option) => option.ids),
        ),
      ]);
    }

    if (['ulikey', 'vls'].includes(option?.key ?? '') || option?.key === 'address') {
      this.filteringService.ignoreFilters.set(true);
    }

    this.showingSearchOptions.set(false);
    this.focusedIndex.set(0);
  }

  private async _search(): Promise<void> {
    if (!this.searchModel().term) {
      return;
    }

    await SetSearch.call(this.filteringService, this.searchModel().term);
  }

  private _toggleSearchOptions(): void {
    this.showingSearchOptions.update((value) => !value);
  }

  protected debouncedSearch(event: Event): void {
    if (!this.filteringService.userInitiatedSearch()) {
      this.filteringService.userInitiatedSearch.set(true);
    }

    this.showingSearchOptions.set(true);
    this.focusedIndex.set(0);

    const _value = (event.target as HTMLInputElement).value;
    if (_value.length === 0) {
      this.searchModel.update((model) => ({ ...model, term: '' }));
      this.filteringService.search.set('');
      this.filteringService.searchModels.set([]);
      this.filteringService.models.set([]);
      this.filteringService.ids.set([]);
      this.filteringService.searchOptionProps.set([]);
      this.filteringService.searchOptions.set([]);
      this.showingSearchOptions.set(false);
    }
    clearTimeout(this._searchTimeout);
    this._searchTimeout = setTimeout(() => {
      if (this.searchModel().term.length >= 3) {
        void this._search();
      }
    }, 300);
  }

  protected handleKeyboardNavigation(event: KeyboardEvent): void {
    const _searchOptions = this.filteringService.searchOptions() as SearchOption[];
    const _optionCount = _searchOptions.length;
    const _element = document.getElementById(this.focusedOption()?.uxid ?? '');

    if (event.code === 'ArrowDown') {
      event.preventDefault();
      if (_optionCount === 0) return;

      const _currentIndex = this.focusedIndex();
      const _nextIndex = (_currentIndex + 1) % _optionCount;
      if (_nextIndex > 0 && _nextIndex < _optionCount) {
        _element?.scrollIntoView({ block: 'center', inline: 'nearest' });
        this.focusedIndex.set(_nextIndex);
        this.showingSearchOptions.set(true);
      }
    } else if (event.code === 'ArrowUp') {
      event.preventDefault();
      if (_optionCount === 0) return;

      const _currentIndex = this.focusedIndex();
      const _nextIndex = (_currentIndex - 1 + _optionCount) % _optionCount;
      if (_nextIndex < _optionCount - 1) {
        _element?.scrollIntoView({ block: 'center', inline: 'nearest' });
        this.focusedIndex.set(_nextIndex);
        this.showingSearchOptions.set(true);
      }
    } else if (event.code === 'Escape') {
      event.preventDefault();
      this.focusedIndex.set(0);
      // Keep dropdown open, just clear focus
    } else if (event.code === 'Enter') {
      const _focusedIdx = this.focusedIndex();
      if (_focusedIdx < _optionCount) {
        event.preventDefault();
        const _selectedOption = _searchOptions[_focusedIdx];
        this._applySearch(_selectedOption);
      }
      // Otherwise, let the form submit naturally
    }
  }

  protected onFocusOut(): void {
    this.showingSearchOptions.set(false);
  }

  protected onSubmit(event: Event, selectedOption?: SearchOption): void {
    event.preventDefault();
    this._applySearch(selectedOption);
  }
}
